// src/utils/menuItemUtils.js

/**
 * Get all cart variants for a given item
 * @param {Object} cartItems - Cart items object
 * @param {Object} item - Menu item
 * @returns {Array} Array of variant IDs in cart
 */
export const getAllCartVariants = (cartItems, item) => {
  const baseId = item.id || item.name;
  return Object.keys(cartItems).filter((cartId) => cartId.startsWith(baseId));
};

/**
 * Get the total quantity of an item in cart (across all variants)
 * @param {Object} cartItems - Cart items object
 * @param {Object} item - Menu item
 * @returns {number} Total quantity in cart
 */
export const getItemQuantityInCart = (cartItems, item) => {
  const variants = getAllCartVariants(cartItems, item);
  return variants.reduce((total, variantId) => total + cartItems[variantId], 0);
};

/**
 * Create a unique item ID - no longer encodes modifiers in the ID
 * @param {string} baseId - Base item ID
 * @returns {string} Base item ID (modifiers should be passed separately)
 */
export const createUniqueItemId = (baseId) => {
  // We no longer embed modifiers in the ID
  // This simplifies the ID system and avoids encoding/decoding issues
  // Modifiers should be passed separately as objects
  return baseId;
};

/**
 * Format selected modifiers into an array format suitable for the cart
 * @param {Object} selectedModifiers - Selected modifiers object (groupId -> modifierId)
 * @param {Array} modifierGroups - The full modifier groups data
 * @returns {Array} Array of modifier objects with id, name, price
 */
export const formatModifiersForCart = (
  selectedModifiers,
  modifierGroups = [],
) => {
  if (!selectedModifiers || Object.keys(selectedModifiers).length === 0) {
    return [];
  }

  const formattedModifiers = [];

  Object.entries(selectedModifiers).forEach(([groupId, selection]) => {
    // Find the modifier group
    const group = modifierGroups.find((g) => g.id === groupId);
    if (!group || !group.modifiers) return;

    // Handle both single selection and multi-selection
    const modifierIds = Array.isArray(selection) ? selection : [selection];

    modifierIds.forEach((modId) => {
      const modifier = group.modifiers.find((m) => m.id === modId);
      if (modifier) {
        formattedModifiers.push({
          id: modifier.id,
          name: modifier.name,
          price: modifier.price || 0,
          groupId,
        });
      }
    });
  });

  return formattedModifiers;
};

/**
 * Extract modifier information from variant ID
 * @param {string} variantId - Variant ID
 * @param {Array} modifierGroups - Item's modifier groups
 * @returns {string} Human-readable modifier description
 */
export const extractModifierFromVariantId = (variantId, modifierGroups) => {
  if (!variantId.includes('::') || !modifierGroups) {
    return null;
  }

  const parts = variantId.split('::');
  if (parts.length < 2) {
    return null;
  }

  // Parse modifier string
  const modifierString = parts[1];
  const modifierPairs = modifierString.split('|');

  const modifierNames = [];
  modifierPairs.forEach((pair) => {
    const [groupId, selectionId] = pair.split(':');
    const group = modifierGroups.find((g) => g.id === groupId);

    if (group) {
      const selectionIds = selectionId.split(',');
      selectionIds.forEach((id) => {
        const modifier = group.modifiers.find((m) => m.id === id);
        if (modifier) {
          modifierNames.push(modifier.name);
        }
      });
    }
  });

  return modifierNames.length > 0 ? modifierNames.join(', ') : null;
};
